import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Ambulance, MessageCircle, Star } from "lucide-react";
import { HeroFade } from "@/lib/motion";

/**
 * Full-screen hero slider — smooth cross-fade between images with configurable display mode.
 * mode: "cover" (stretch/fill) | "contain" (fit) | "zoom" (cover + Ken Burns zoom)
 */
export function SliderHero({ site, doctors }) {
  const images = (site?.hero_images && site.hero_images.length > 0)
    ? site.hero_images
    : ["https://images.unsplash.com/photo-1587351021821-f871837248c6?w=1800&q=85"];
  const mode = site?.hero_image_mode || "zoom";
  const duration = Math.max(2, site?.hero_slide_duration || 6);
  const phone = site?.phone || "0341-9300560";
  const whatsapp = site?.whatsapp || "923419300560";

  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), duration * 1000);
    return () => clearInterval(t);
  }, [images.length, duration]);

  const currentSrc = images[idx];

  // Object-fit css for each mode
  const fitCls = mode === "contain" ? "object-contain bg-slate-900" : "object-cover";

  const heroTitle = site?.hero_title || "Compassionate Care, Trusted Expertise";
  const parts = heroTitle.split(/,\s*/);
  const first = parts[0];
  const rest = parts.slice(1).join(", ");

  return (
    <section className="relative overflow-hidden bg-slate-900" data-testid="hero-slider" style={{ minHeight: "min(88vh, 780px)" }}>
      {/* Cross-fade image stack */}
      <div className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.div
            key={currentSrc + idx}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.img
              src={currentSrc}
              alt=""
              className={`absolute inset-0 w-full h-full ${fitCls}`}
              draggable={false}
              // Ken Burns effect only in "zoom" mode
              initial={mode === "zoom" ? { scale: 1.05 } : { scale: 1 }}
              animate={mode === "zoom" ? { scale: 1.18 } : { scale: 1 }}
              transition={{ duration: duration + 1.4, ease: "linear" }}
            />
          </motion.div>
        </AnimatePresence>

        {/* Dark gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/60 to-slate-950/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/20" />
      </div>

      {/* Content overlay */}
      <div className="relative min-h-inherit">
        <div className="container-medi min-h-[inherit] flex flex-col justify-center py-20 lg:py-28">
          <div className="max-w-2xl text-white">
            <HeroFade>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6 backdrop-blur">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-red-100">{site?.hero_badge || "Trusted Healthcare Since 2001"}</span>
              </div>
            </HeroFade>
            <HeroFade delay={0.1}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-heading font-semibold leading-[1.05] drop-shadow-lg">
                {first}{rest ? "," : ""}<br />
                {rest && <span className="text-red-400">{rest}.</span>}
              </h1>
            </HeroFade>
            <HeroFade delay={0.2}>
              <p className="mt-6 text-lg lg:text-xl text-slate-200 leading-relaxed max-w-xl drop-shadow">
                {site?.hero_subtitle || "24/7 emergency care, modern diagnostics and a team of specialists dedicated to putting patients first."}
              </p>
            </HeroFade>
            <HeroFade delay={0.3}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/doctors" className="inline-flex items-center gap-2 bg-white text-slate-950 hover:bg-red-50 font-semibold rounded-full px-6 py-3.5 transition group" data-testid="hero-find-doctor">
                  Find a Doctor <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-full px-6 py-3.5 transition" data-testid="hero-whatsapp">
                  <MessageCircle className="w-4 h-4" /> WhatsApp Us
                </a>
                <a href={`tel:${phone}`} className="inline-flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white font-medium rounded-full px-6 py-3.5 transition" data-testid="hero-emergency-call">
                  <Ambulance className="w-4 h-4" /> {phone}
                </a>
              </div>
            </HeroFade>
            <HeroFade delay={0.4}>
              <div className="mt-8 flex items-center gap-4">
                <div className="flex -space-x-2">
                  {doctors.slice(0, 4).map((d) => d.photo_url ? <img key={d.id} src={d.photo_url} alt={d.name} className="w-10 h-10 rounded-full ring-2 ring-white/30 object-cover" /> : null)}
                </div>
                <div>
                  <div className="flex items-center gap-1 text-amber-400">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" strokeWidth={0} />)}
                    <span className="ml-1.5 text-sm font-semibold text-white">4.9</span>
                  </div>
                  <div className="text-xs text-slate-300">Rated by 2,300+ patients</div>
                </div>
              </div>
            </HeroFade>
          </div>
        </div>
      </div>

      {/* Slide dots */}
      {images.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10" data-testid="hero-slider-dots">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-500 ${i === idx ? "w-10 bg-white" : "w-2 bg-white/40 hover:bg-white/70"}`}
              data-testid={`hero-slide-dot-${i}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
